import { createFileRoute } from "@tanstack/react-router";
import { InfoPage } from "@/components/InfoPage";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | Picamini.online" },
      {
        name: "description",
        content:
          "Read the Picamini.online Privacy Policy to understand how our free classroom and PDF tools handle information, cookies, analytics, and advertising.",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Privacy Policy | Picamini.online" },
      { property: "og:description", content: "Learn how Picamini.online handles browser data, cookies, advertising, local storage, and privacy requests." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://picamini.online/privacy-policy" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Privacy Policy | Picamini.online" },
      { name: "twitter:description", content: "Picamini.online privacy, cookies, local storage, and advertising information." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { property: "og:locale", content: "en_US" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/privacy-policy" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/privacy-policy" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/privacy-policy" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://picamini.online/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Privacy Policy",
              "item": "https://picamini.online/privacy-policy"
            }
          ]
        })
      }
    ]
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <InfoPage
      eyebrow="Privacy and transparency"
      title="Privacy Policy"
      description="This policy explains what information Picamini.online collects, how the site uses it, and the choices available to visitors."
    >
      <p>
        <strong>Effective date: September 6, 2026</strong>
      </p>
      <p>
        Picamini.online (&quot;we,&quot; &quot;our,&quot; or &quot;the site&quot;) provides free browser-based classroom,
        decision-making, and PDF tools. We respect your privacy and design many tools so that
        information entered into them remains on your device.
      </p>
      <h2>Who this policy applies to</h2>
      <p>
        This policy applies to visitors who browse Picamini.online, use our free tools, contact us,
        or interact with our pages and advertisements. It explains our general information
        practices and should be read together with our <a href="/terms-of-use">Terms of Use</a> and{" "}
        <a href="/disclaimer">Disclaimer</a>.
      </p>
      <h2>Information you enter into tools</h2>
      <p>
        Many tools allow you to enter names, lists, choices, notes, or files. For those client-side
        tools, the information is processed in your browser and is not intentionally uploaded to
        our servers. Some settings may be stored in your browser&apos;s local storage so the tool can
        remember them on the same device. You can remove that data by clearing the site data in
        your browser.
      </p>
      <p>
        Do not enter confidential student records, health information, passwords, financial
        information, or other sensitive data into a public website unless you have confirmed that
        the tool and your organization&apos;s policy allow it.
      </p>
      <h2>How we use information</h2>
      <p>Information available to us may be used to:</p>
      <ul>
        <li>Provide, maintain, secure, and improve Picamini.online</li>
        <li>Respond to messages, support requests, and accessibility feedback</li>
        <li>Understand general traffic and diagnose technical problems</li>
        <li>Prevent abuse, fraud, spam, and activity that could harm visitors or the site</li>
        <li>Display and measure advertising in accordance with applicable policies and law</li>
        <li>Meet legal, regulatory, or security obligations when required</li>
      </ul>
      <h2>Information collected automatically</h2>
      <p>
        Like most websites, our hosting, security, and performance services may process limited
        technical information such as IP address, browser type, device type, referring page, and
        approximate time of access. This information may be used to deliver the site, prevent
        abuse, diagnose errors, and understand general usage. We do not sell personal information.
      </p>
      <h2>Cookies and local storage</h2>
      <p>
        Picamini.online may use essential cookies, browser storage, or similar technologies to
        maintain preferences, support site functionality, and measure performance. These
        technologies do not give us access to the contents of your local tool data. You can control
        cookies and clear local storage through your browser settings, although some features may
        not work correctly afterward.
      </p>
      <h2>Advertising</h2>
      <p>
        We may use Google AdSense or other advertising partners to support the free operation of
        this site. These partners may use cookies or similar technologies to show and measure ads,
        subject to their own policies and applicable law. Google may use advertising cookies to
        personalize ads based on a visitor&apos;s browsing activity. Visitors can learn more about
        Google&apos;s advertising practices and manage available ad settings at{" "}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
          Google Ads Settings
        </a>
        .
      </p>
      <p>
        We do not control the specific advertisements that may appear. Ad relevance, measurement,
        and personalization can depend on a visitor&apos;s settings, region, consent choices, and the
        policies of the advertising provider. Please review the provider&apos;s settings and privacy
        notices for more information.
      </p>
      <h2>Third-party links and services</h2>
      <p>
        The site may link to third-party websites or load third-party libraries and services.
        Those providers operate under their own privacy policies. We are not responsible for the
        privacy practices, security, or content of external websites.
      </p>
      <h2>Children&apos;s privacy</h2>
      <p>
        Picamini.online is intended for general educational use by adults, educators, families,
        and students with appropriate supervision. We do not knowingly request personal information
        from children. Teachers and guardians should guide children in avoiding the submission of
        personal or confidential information.
      </p>
      <h2>Data security and retention</h2>
      <p>
        We use reasonable measures to keep the site available and protect operational information.
        No internet service can guarantee absolute security. Information held by hosting,
        analytics, or advertising providers is retained according to their policies and legal
        obligations.
      </p>
      <h2>Your choices and privacy requests</h2>
      <p>
        You can disable cookies through your browser, clear Picamini.online local storage, decline
        optional advertising personalization where available, and stop using the site at any time.
        Depending on your location, you may also have rights to request access, correction, deletion,
        restriction, or information about processing. To make a privacy request, contact us with
        enough detail to understand your request. We may need to verify the request before acting.
      </p>
      <h2>International visitors</h2>
      <p>
        Picamini.online may be hosted or supported by providers operating in countries different
        from your own. Technical information may therefore be processed where our providers
        operate, subject to applicable safeguards and law.
      </p>
      <h2>Changes to this policy</h2>
      <p>
        We may update this Privacy Policy when our services, legal requirements, or advertising
        partners change. The updated version will be posted on this page with a revised effective
        date.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about this policy can be sent to{" "}
        <a href="mailto:support@picamini.online">support@picamini.online</a>. Please see our{" "}
        <a href="/contact">Contact page</a> for additional guidance.
      </p>
    </InfoPage>
  );
}
